import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Icons } from "../../constants/icon-library";
import { getAvatarColor } from "../Meetings/Meetings.utils";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  sendTeamMemberInvite,
  fetchAllMembers,
  removeTeamMemberByUuid,
} from "../../features/team-member/teamMemberSlice";
import { useDispatch, useSelector } from "react-redux";
import { confirmationModal } from "../../constants/confirmation.constants";
import LoadingComponent from "../../components/common/LoadingComponent";

const validationSchema = Yup.object({
  email: Yup.string()
    .trim()
    .required("Email is required")
    .matches(
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
      "Please enter a valid email address",
    ),
});

function TeamMemberList() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const { members, loading } = useSelector((state) => state.teamMember);
  const { user } = useSelector((state) => state.user);

  const isOwner = members.some(
    (member) => member.uuid === user?.uuid && member.role === "Owner",
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAllMembers());
  }, [dispatch]);

  const handleOpenModal = () => {
    setShowInviteModal(true);
  };

  const handleCloseModal = () => {
    setShowInviteModal(false);
  };

  const handleSendInvite = async (email) => {
    try {
      setShowInviteModal(false);

      const response = await dispatch(
        sendTeamMemberInvite({ invitationEmail: email }),
      ).unwrap();

      dispatch(fetchAllMembers());
      toast.success(response.message || `Invite sent to ${email}`);
    } catch (error) {
      toast.error(error?.message || "Failed to send invite");
    }
  };

  const handleRemoveMember = async (uuid) => {
    const result = await confirmationModal({
      title: "Are you sure?",
      text: "This team member will lose access to your organization and its shared resources",
      confirmText: "Yes, Remove",
      cancelText: "Cancel",
    });

    if (result.isConfirmed) {
      dispatch(removeTeamMemberByUuid(uuid));
      toast.success("Member removed");
    }
  };

  return (
    <div className="container py-12">
      <div className="card border-0 shadow-lg">
        <div className="card-body p-5 rounded-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="mb-1 fw-semibold">Team Members</h3>
              <small className="text-muted">
                {members.length} {members.length === 1 ? "member" : "members"}{" "}
                in your workspace
              </small>
            </div>

            <button
              className="btn btn-primary btn-sm d-flex align-items-center gap-2"
              onClick={handleOpenModal}
            >
              <i
                className={`wb ${Icons("plus") || Icons("add") || Icons("user")}`}
              />
              Invite Member
            </button>
          </div>

          <hr className="border-secondary opacity-25 mb-4" />

          <div className="table-responsive">
            {loading || !members ? (
              <LoadingComponent loading size={15} />
            ) : (
              <table className="table align-middle">
                <thead>
                  <tr className="text-muted">
                    <th scope="col" className="fw-semibold small">
                      Member
                    </th>
                    <th scope="col" className="fw-semibold small">
                      Email
                    </th>
                    <th scope="col" className="fw-semibold small">
                      Role
                    </th>

                    {isOwner && (
                      <th scope="col" className="fw-semibold small text-end">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr key={member.uuid}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                            style={{
                              width: 40,
                              height: 40,
                              background: getAvatarColor(member.name),
                              color: "#022c22",
                              fontSize: "0.9rem",
                            }}
                          >
                            {member.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="fw-medium">{member.name}</span>
                        </div>
                      </td>
                      <td className="text-muted">{member.email}</td>
                      <td>
                        <span
                          className={`badge bg-primary rounded-pill px-3 py-2`}
                        >
                          {member.role}
                        </span>
                      </td>
                      {isOwner && (
                        <td className="text-end">
                          {member.role !== "Owner" && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveMember(member.uuid)}
                            >
                              <i className={`wb ${Icons("delete")}`} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showInviteModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <Formik
                initialValues={{ email: "" }}
                validationSchema={validationSchema}
                onSubmit={async (values, { resetForm }) => {
                  const success = await handleSendInvite(values.email);

                  if (success) {
                    resetForm();
                    handleCloseModal();
                  }
                }}
              >
                {({ errors, touched, isValid }) => (
                  <Form>
                    <div className="modal-header border-0 pb-0">
                      <h5 className="modal-title fw-semibold">
                        Invite Team Member
                      </h5>
                    </div>

                    <div className="modal-body pt-3">
                      <div className="mb-3">
                        <label className="form-label text-muted fw-semibold small d-flex align-items-center gap-2 mb-2">
                          <i className={`wb ${Icons("email")}`} />
                          Email Address
                        </label>

                        <Field
                          name="email"
                          placeholder="name@example.com"
                          autoComplete="off"
                          className={`form-control rounded-3 py-3 ${
                            touched.email && errors.email ? "is-invalid" : ""
                          }`}
                        />

                        <ErrorMessage
                          name="email"
                          component="div"
                          className="invalid-feedback d-block"
                        />
                      </div>
                    </div>

                    <div className="modal-footer border-0 pt-0">
                      <button
                        type="button"
                        className="btn btn-outline-danger px-4"
                        onClick={handleCloseModal}
                      >
                        Cancel
                      </button>

                      <button type="submit" className="btn btn-primary px-4">
                        Send Invite
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamMemberList;
